<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Image Component
      p.text--secondary A versatile image component based on Vuetify's VImg with lazy loading and aspect ratio support.

    // Image Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in imageVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped images (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="img in variation.images" :key="img.label" class="d-flex flex-column align-center")
                  yz-img(v-bind="img.props")
                  small.text--secondary.mt-2(v-if="img.hint") {{ img.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="img in variation.images" :key="img.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === img.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === img.code ? 'success' : ''" @click="copyCode(img.code)")
                    span.font-weight-medium.ml-2 {{ img.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ img.code }}

          // Individual images
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-img(v-bind="variation.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in imageProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      imageVariations: [
        {
          name: 'Basic Images',
          id: 'basic-images',
          description: 'Standard images with different sources',
          isGroup: true,
          images: [
            {
              label: 'Default',
              props: {
                src: 'https://picsum.photos/200/150',
                width: '200',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/200/150" width="200" height="150")',
              hint: '200x150'
            },
            {
              label: 'Square',
              props: {
                src: 'https://picsum.photos/150/150',
                width: '150',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/150/150" width="150" height="150")',
              hint: '150x150'
            },
            {
              label: 'Avatar Style',
              props: {
                src: 'https://picsum.photos/100/100',
                width: '100',
                height: '100',
                class: 'rounded-circle'
              },
              code: 'yz-img.rounded-circle(src="https://picsum.photos/100/100" width="100" height="100")',
              hint: 'Circular'
            }
          ]
        },
        {
          name: 'Aspect Ratios',
          id: 'aspect-ratios',
          description: 'Images with different aspect ratios maintained',
          isGroup: true,
          images: [
            {
              label: '16:9',
              props: {
                src: 'https://picsum.photos/320/180',
                aspectRatio: 16/9,
                width: '320'
              },
              code: 'yz-img(src="https://picsum.photos/320/180" :aspect-ratio="16/9" width="320")',
              hint: 'Widescreen'
            },
            {
              label: '4:3',
              props: {
                src: 'https://picsum.photos/240/180',
                aspectRatio: 4/3,
                width: '240'
              },
              code: 'yz-img(src="https://picsum.photos/240/180" :aspect-ratio="4/3" width="240")',
              hint: 'Standard'
            },
            {
              label: '1:1',
              props: {
                src: 'https://picsum.photos/180/180',
                aspectRatio: 1,
                width: '180'
              },
              code: 'yz-img(src="https://picsum.photos/180/180" :aspect-ratio="1" width="180")',
              hint: 'Square'
            }
          ]
        },
        {
          name: 'Lazy Loading',
          id: 'lazy-loading',
          description: 'Images with lazy loading enabled',
          isGroup: true,
          images: [
            {
              label: 'Lazy Load',
              props: {
                src: 'https://picsum.photos/200/150',
                lazySrc: 'https://via.placeholder.com/200x150/cccccc/666666?text=Loading...',
                width: '200',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/200/150" lazy-src="https://via.placeholder.com/200x150/cccccc/666666?text=Loading..." width="200" height="150")',
              hint: 'With placeholder'
            },
            {
              label: 'Eager Load',
              props: {
                src: 'https://picsum.photos/200/150',
                eager: true,
                width: '200',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/200/150" eager width="200" height="150")',
              hint: 'Immediate load'
            }
          ]
        },
        {
          name: 'Rounded Corners',
          id: 'rounded-corners',
          description: 'Images with different border radius values',
          isGroup: true,
          images: [
            {
              label: 'Rounded',
              props: {
                src: 'https://picsum.photos/150/150',
                width: '150',
                height: '150',
                class: 'rounded'
              },
              code: 'yz-img.rounded(src="https://picsum.photos/150/150" width="150" height="150")',
              hint: 'Small radius'
            },
            {
              label: 'Rounded Large',
              props: {
                src: 'https://picsum.photos/150/150',
                width: '150',
                height: '150',
                class: 'rounded-lg'
              },
              code: 'yz-img.rounded-lg(src="https://picsum.photos/150/150" width="150" height="150")',
              hint: 'Large radius'
            },
            {
              label: 'Circle',
              props: {
                src: 'https://picsum.photos/150/150',
                width: '150',
                height: '150',
                class: 'rounded-circle'
              },
              code: 'yz-img.rounded-circle(src="https://picsum.photos/150/150" width="150" height="150")',
              hint: 'Full circle'
            }
          ]
        },
        {
          name: 'Gradients and Overlays',
          id: 'gradients-overlays',
          description: 'Images with gradient overlays and special effects',
          isGroup: true,
          images: [
            {
              label: 'Gradient Overlay',
              props: {
                src: 'https://picsum.photos/200/150',
                gradient: 'to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)',
                width: '200',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/200/150" gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)" width="200" height="150")',
              hint: 'Dark gradient'
            },
            {
              label: 'Position Cover',
              props: {
                src: 'https://picsum.photos/400/200',
                position: 'center center',
                contain: false,
                width: '200',
                height: '150'
              },
              code: 'yz-img(src="https://picsum.photos/400/200" position="center center" :contain="false" width="200" height="150")',
              hint: 'Cover mode'
            }
          ]
        },
        {
          name: 'Responsive Sizes',
          id: 'responsive-sizes',
          description: 'Images with different sizes for responsive design',
          isGroup: true,
          images: [
            {
              label: 'Small',
              props: {
                src: 'https://picsum.photos/80/80',
                width: '80',
                height: '80'
              },
              code: 'yz-img(src="https://picsum.photos/80/80" width="80" height="80")',
              hint: '80x80'
            },
            {
              label: 'Medium',
              props: {
                src: 'https://picsum.photos/120/120',
                width: '120',
                height: '120'
              },
              code: 'yz-img(src="https://picsum.photos/120/120" width="120" height="120")',
              hint: '120x120'
            },
            {
              label: 'Large',
              props: {
                src: 'https://picsum.photos/200/200',
                width: '200',
                height: '200'
              },
              code: 'yz-img(src="https://picsum.photos/200/200" width="200" height="200")',
              hint: '200x200'
            }
          ]
        }
      ],
      imageProps: [
        {
          name: 'src',
          type: 'String',
          default: 'undefined',
          description: 'The image source URL'
        },
        {
          name: 'lazySrc',
          type: 'String',
          default: 'undefined',
          description: 'Placeholder image source while loading'
        },
        {
          name: 'alt',
          type: 'String',
          default: 'undefined',
          description: 'Alt text for accessibility'
        },
        {
          name: 'aspectRatio',
          type: 'Number',
          default: 'undefined',
          description: 'Aspect ratio to maintain (width/height)'
        },
        {
          name: 'contain',
          type: 'Boolean',
          default: 'false',
          description: 'Use object-fit: contain instead of cover'
        },
        {
          name: 'eager',
          type: 'Boolean',
          default: 'false',
          description: 'Disable lazy loading'
        },
        {
          name: 'gradient',
          type: 'String',
          default: 'undefined',
          description: 'CSS gradient overlay string'
        },
        {
          name: 'height',
          type: 'Number | String',
          default: 'undefined',
          description: 'Image height in pixels'
        },
        {
          name: 'width',
          type: 'Number | String',
          default: 'undefined',
          description: 'Image width in pixels'
        },
        {
          name: 'maxHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum height constraint'
        },
        {
          name: 'maxWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum width constraint'
        },
        {
          name: 'minHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum height constraint'
        },
        {
          name: 'minWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum width constraint'
        },
        {
          name: 'position',
          type: 'String',
          default: 'center center',
          description: 'CSS background-position value'
        },
        {
          name: 'transition',
          type: 'String | Boolean',
          default: 'fade-transition',
          description: 'Transition component to use'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-4 > *:not(:last-child) {
  margin-right: 16px;
}
</style>